import ja from 'date-fns/locale/ja';
import { createRef } from 'react';
import DatePicker, { ReactDatePicker, registerLocale } from 'react-datepicker';
import './_datepicker.scss';

import { images } from '@utils/constants';
import { dateHelper, stringHelper, validateHelper } from '@utils/helpers';
import { IDatepickerComponent, IDatepickerComponentProps } from '~/interfaces/components/datepicker';

const Datepicker: IDatepickerComponent<IDatepickerComponentProps> = (props) => {
    const {
        className,
        fontSize,
        dateFormat,
        timeFormat,
        width,
        showTimeInput,
        location,
        height,
        placeholder,
        dateTimeFormat,
        onChange,
        value,
        maxDate,
        onBlur,
        innerClassName,
        disabled,
        showTimeSelect,
        minTime,
        maxTime,
        isError,
        isErrorNonMessage,
        showMonthYearPicker,
        showYearDropDown,
        popperPlacement,
        classNameIcon = ''
    } = props;
    let { minDate } = props;
    minDate = minDate ?? dateHelper.formatToDate('2023/01/01');

    const dateRef = createRef<ReactDatePicker>();
    registerLocale('ja', ja);
    const timeProps = {
        dateFormat: dateTimeFormat,
        timeFormat,
        showTimeInput: true,
        placeholderText: '',
    };

    const checkDate = (date: Date) => {
        return (
            (maxDate &&
                !minDate &&
                dateHelper.compareDateSameOrBefore(
                    dateHelper.formatDate(date?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                    dateHelper.formatDate(maxDate?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                )) ||
            (minDate &&
                !maxDate &&
                dateHelper.compareDateSameOrAfter(
                    dateHelper.formatDate(date?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                    dateHelper.formatDate(minDate?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                )) ||
            (minDate &&
                maxDate &&
                dateHelper.compareDateSameOrAfter(
                    dateHelper.formatDate(date?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                    dateHelper.formatDate(minDate?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                ) &&
                dateHelper.compareDateSameOrBefore(
                    dateHelper.formatDate(date?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                    dateHelper.formatDate(maxDate?.toISOString() ?? dateHelper.getUTCStringNow(), 'YYYY-MM-DD'),
                ))
        );
    };

    const handleInputDate = (val: string) => {
        const value = stringHelper.formatHalfWidthText(val);
        if (onChange) {
            if (value && validateHelper.isNumber(value)) {
                if (value.length > 8) {
                    onChange(dateHelper.getDate());
                } else {
                    if (value.length === 8) {
                        const date = dateHelper.formatFromText(value);
                        if (dateHelper.isValidDate(date)) {
                            if (checkDate(date)) {
                                onChange(date);
                            } else {
                                onChange(dateHelper.getDate());
                            }
                        } else {
                            onChange(dateHelper.getDate());
                        }
                    } else {
                        onChange(dateHelper.getDate());
                    }
                }
            } else {
                if (!dateHelper.isValid(value) && value !== '') {
                    onChange(dateHelper.getDate());
                } else if (dateHelper.isValid(value)) {
                    const date = dateHelper.formatToDate(value, 'YYYY-MM-DD HH:mm:ss');
                    if (date && checkDate(date)) {
                        onChange(date);
                    } else {
                        onChange(dateHelper.getDate());
                    }
                }
            }
        }
    };

    return (
        <div
            className={`components__datepicker ${
                showTimeInput ? '' : 'position-relative'
            } bases__width--${width} bases__height--${height} ${className} ${
                showYearDropDown ? 'show__year--dropdown' : 'hide__year--dropdown'
            }`}>
            <DatePicker
                ref={dateRef}
                className={`w-100 components__datepicker-input bases__font--${fontSize} ${innerClassName} ${
                    isError || isErrorNonMessage ? 'components__datepicker-error' : ''
                }`}
                selected={value}
                onBlur={(event: any) => handleInputDate(event.currentTarget.value)}
                onChange={(value: Date) => (onChange ? onChange(value) : {})}
                peekNextMonth={true}
                showMonthDropdown={true}
                showYearDropdown={true}
                showMonthYearPicker={showMonthYearPicker}
                showFullMonthYearPicker={true}
                showTwoColumnMonthYearPicker
                dropdownMode="select"
                locale={location}
                minDate={minDate}
                maxDate={maxDate}
                onKeyDown={(e) => {
                    e.preventDefault();
                }}
                onCalendarClose={() => (onBlur ? onBlur() : {})}
                {...(showTimeInput
                    ? timeProps
                    : {
                          dateFormat,
                          placeholderText: placeholder,
                      })}
                minTime={minTime}
                maxTime={maxTime}
                showTimeSelect={showTimeSelect}
                timeIntervals={1}
                disabled={disabled}
                dateFormat={dateFormat}
                popperPlacement={popperPlacement}
            />
            {showTimeInput ? (
                <></>
            ) : (
                <img
                    className={`components__datepicker-img bases__filter--dark-gray position-absolute ${classNameIcon}`}
                    src={images.IMAGES.CALENDAR}
                    onClick={() => dateRef.current?.setFocus()}
                />
            )}
        </div>
    );
};

Datepicker.defaultProps = {
    className: '',
    fontSize: '14',
    dateFormat: 'yyyy/MM/dd',
    width: '',
    height: '',
    showTimeInput: false,
    location: 'ja',
    placeholder: 'yyyy/mm/dd',
    dateTimeFormat: 'yyyy/MM/dd HH:mm',
    value: null,
    innerClassName: '',
};

export default Datepicker;
